import React, { useState, useCallback, useMemo } from 'react';
import {
  CheckSquare,
  Search,
  Clock,
  ExternalLink,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { useCachedData } from '../hooks/useCachedData';
import { useStickyFilter } from '../hooks/useStickyFilter';

const ChecklistTasks = ({ currentUser }) => {
  const fetchFMSData = useCallback(async () => {
    const spreadsheetId = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_ID_FMS;
    const sheetName = process.env.REACT_APP_GOOGLE_SHEETS_SPREADSHEET_NAME_FMS;
    const params = new URLSearchParams({ sheetId: spreadsheetId, sheetName, range: 'A1:M' });
    const response = await fetch(`/api/sheets?${params}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    const rows = data.values || [];
    if (rows.length === 0) return [];
    const headers = rows[0];
    return rows.slice(1)
      .filter(row => row && row.length > 0)
      .map((row, index) => {
        const task = {};
        headers.forEach((header, colIndex) => {
          if (header && row[colIndex] !== undefined) {
            task[header.toLowerCase().replace(/\s+/g, '_')] = row[colIndex];
          }
        });
        task.id = index + 1;
        task.rowNumber = index + 2;
        return task;
      })
      .filter(task => {
        const doer = task.doer || task.assignedto || '';
        return doer === currentUser.name;
      });
  }, [currentUser.name]);

  const { data: fmsData, loading, error } = useCachedData('fms', currentUser, fetchFMSData);
  const [searchTerm, setSearchTerm] = useState('');
  // Search / filter card stays pinned while the list scrolls under it.
  const [filterSentinelRef, isFilterStuck] = useStickyFilter();

  // Helper functions
  const getTaskInfo = (task) => {
    return task.task_info || task.taskinfo || task.what_to_do || '';
  };

  // Helper function to parse and extract info from task description
  const parseTaskInfo = (task) => {
    const taskInfo = getTaskInfo(task);
    
    // Extract frequency, task ID, and task date from the task description
    const freqMatch = taskInfo.match(/Freq:-([^,\s]+)/);
    const taskIdMatch = taskInfo.match(/Task ID:-(\d+)/);
    const taskDateMatch = taskInfo.match(/Task Date:-([^$]+)$/);
    
    // Clean task description by removing the extracted parts
    let cleanedTask = taskInfo;
    if (freqMatch) {
      cleanedTask = cleanedTask.replace(/\s*Freq:-[^,\s]+,?\s*/g, '');
    }
    if (taskIdMatch) {
      cleanedTask = cleanedTask.replace(/\s*Task ID:-\d+,?\s*/g, '');
    }
    if (taskDateMatch) {
      cleanedTask = cleanedTask.replace(/\s*Task Date:-[^$]+$/g, '');
    }
    
    // Clean up extra spaces and commas
    cleanedTask = cleanedTask.replace(/\s*,\s*$/, '').replace(/\s+/g, ' ').trim();
    
    return {
      cleanedTask: cleanedTask || 'No description available',
      frequency: freqMatch ? freqMatch[1] : '',
      taskId: taskIdMatch ? taskIdMatch[1] : '',
      taskDate: taskDateMatch ? taskDateMatch[1].trim() : ''
    };
  };

  const getWhatToDo = (task) => {
    return task.what_to_do || task.whattodo || '';
  };

  const getDoer = (task) => {
    return task.doer || task.assignedTo || '';
  };

  const getFMSType = (task) => {
    return task.fms || task.fms_type || 'Unknown';
  };

  const getDeadline = (task) => {
    return task.deadline || task.due_date || task.duedate || '';
  };

  const getLink = (task) => {
    return task.link || '';
  };

  // Helper function to get task frequency - now extracted from task description
  const getFrequency = (task) => {
    const parsed = parseTaskInfo(task);
    return parsed.frequency || task.frequency || task.freq || 'N/A';
  };

  // Helper function to get task ID - now extracted from task description
  const getTaskId = (task) => {
    const parsed = parseTaskInfo(task);
    return parsed.taskId || task.task_id || task.taskid || task.id || 'N/A';
  };

  // Helper function to get task date - now extracted from task description
  const getTaskDate = (task) => {
    const parsed = parseTaskInfo(task);
    return parsed.taskDate || task.task_date || task.taskdate || getDeadline(task) || 'N/A';
  };

  // Helper function to get planned date
  const getPlanned = (task) => {
    return task.planned || task.planned_date || '';
  };

  // Helper function to calculate delay from planned date
  const calculateDelayFromPlanned = (task) => {
    const plannedDate = getPlanned(task);
    if (!plannedDate) return 0;

    try {
      const planned = new Date(plannedDate);
      const today = new Date();
      
      // Reset time to start of day for accurate day calculation
      today.setHours(0, 0, 0, 0);
      planned.setHours(0, 0, 0, 0);
      
      // Calculate difference in days
      const diffTime = today - planned;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      return Math.max(0, diffDays); // Return 0 if not overdue
    } catch (error) {
      console.error('Error calculating delay from planned date:', error);
      return 0;
    }
  };

  // Helper function to determine if task is for today
  const isTaskForToday = (task) => {
    const taskDate = getTaskDate(task);
    if (!taskDate) return false;
    
    const today = new Date();
    const taskDateObj = new Date(taskDate);
    
    return taskDateObj.toDateString() === today.toDateString();
  };

  // Check if task is overdue - using planned date calculation
  const isTaskOverdue = (task) => {
    const delayInDays = calculateDelayFromPlanned(task);
    return delayInDays > 0; // Any positive number means overdue
  };

  // Filter FMS data to get only checklist tasks assigned to current user as Doer
  const checklistTasks = useMemo(() => {
    if (!Array.isArray(fmsData)) return [];
    
    let filteredTasks = fmsData.filter(task => {
      // First filter: only checklist tasks
      const fmsType = getFMSType(task);
      const isChecklistTask = fmsType.toLowerCase().includes('checklist task');
      
      // Second filter: tasks where current user is the Doer
      const taskDoer = getDoer(task);
      const isUserDoer = taskDoer === currentUser.name;
      
      return isChecklistTask && isUserDoer;
    });

    // Apply search filter
    if (searchTerm) {
      filteredTasks = filteredTasks.filter(task =>
        getTaskInfo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getWhatToDo(task).toLowerCase().includes(searchTerm.toLowerCase()) ||
        getDoer(task).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredTasks;
  }, [fmsData, currentUser.name, searchTerm]);

  // Group tasks by Today and Overdue with sorting by Planned date
  const groupedTasks = useMemo(() => {
    const groups = {
      today: [],
      overdue: []
    };

    checklistTasks.forEach(task => {
      if (isTaskOverdue(task)) {
        groups.overdue.push(task);
      } else if (isTaskForToday(task)) {
        groups.today.push(task);
      } else {
        // For other tasks, put them in today group by default
        groups.today.push(task);
      }
    });

    // Sort both groups by Planned date in descending order (newest first)
    const sortByPlannedDesc = (a, b) => {
      const plannedA = getPlanned(a);
      const plannedB = getPlanned(b);
      
      if (!plannedA && !plannedB) return 0;
      if (!plannedA) return 1;
      if (!plannedB) return -1;
      
      const dateA = new Date(plannedA);
      const dateB = new Date(plannedB);
      
      return dateB - dateA; // Descending order (newest first)
    };

    groups.today.sort(sortByPlannedDesc);
    groups.overdue.sort(sortByPlannedDesc);

    return groups;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistTasks]);

  // Get summary stats
  const getStats = () => {
    const totalTasks = checklistTasks.length;
    const overdueTasks = groupedTasks.overdue.length;
    const todayTasks = groupedTasks.today.length;
    const completedTasks = checklistTasks.filter(task => {
      const taskInfo = getTaskInfo(task);
      return taskInfo.toLowerCase().includes('completed') || taskInfo.toLowerCase().includes('done');
    }).length;

    return {
      totalTasks,
      overdueTasks,
      todayTasks,
      completedTasks
    };
  };

  const stats = getStats();

  // Render a checklist task row
  const renderChecklistRow = (task, idx, isOverdue) => {
    const parsed = parseTaskInfo(task);
    const tInfo = parsed.cleanedTask;
    const freq = getFrequency(task);
    const tId = getTaskId(task);
    const tDate = getTaskDate(task);
    const planned = getPlanned(task);
    const delayDays = calculateDelayFromPlanned(task);
    const link = getLink(task);
    return (
      <div key={`${isOverdue ? 'ov' : 'td'}-${idx}`} className={`k-task-row ${isOverdue ? 'overdue' : ''}`}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isOverdue ? 'var(--status-error)' : 'var(--forest-400,#4a9e6a)', flexShrink: 0, marginTop: '5px' }}></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="k-task-title">{tInfo || 'No description available'}</p>
          <div className="k-task-meta" style={{ marginTop: '4px', flexWrap: 'wrap' }}>
            {freq && freq !== 'N/A' && <span className="k-task-meta-item">Freq: {freq}</span>}
            {tId && tId !== 'N/A' && <span className="k-task-meta-item">ID: {tId}</span>}
            {tDate && tDate !== 'N/A' && <span className="k-task-meta-item"><Clock size={11} /> {tDate}</span>}
            {planned && <span className="k-task-meta-item" style={{ color: 'var(--forest-400,#4a9e6a)' }}>Planned: {planned}</span>}
            {delayDays > 0 && <span className="k-task-meta-item" style={{ color: 'var(--status-error)' }}>Overdue by {delayDays}d</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {isOverdue && <span className="k-badge k-badge-overdue">Overdue</span>}
          {tDate && tDate !== 'N/A' && <span className="k-badge k-badge-role">{tDate}</span>}
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" title="Open task link">
              <ExternalLink size={14} style={{ color: 'var(--gold-500,#c8a03c)' }} />
            </a>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="k-card">
        <div className="k-loading">
          <div className="k-loading-spinner"></div>
          <p className="k-loading-text">Loading checklist tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="k-card">
        <div className="k-error">
          <AlertTriangle size={36} className="k-error-icon" />
          <p className="k-error-title">Error Loading Checklist</p>
          <p className="k-error-msg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="k-page">
      {/* Stats */}
      <div className="k-stats-grid">
        <div className="k-stat-card">
          <div className="k-stat-icon"><CheckSquare size={20} /></div>
          <p className="k-stat-value">{stats.totalTasks}</p>
          <p className="k-stat-label">Total Tasks</p>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ color: 'var(--status-error)' }}><AlertTriangle size={20} /></div>
          <p className="k-stat-value" style={{ color: 'var(--status-error)' }}>{stats.overdueTasks}</p>
          <p className="k-stat-label">Overdue</p>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ color: 'var(--forest-400,#4a9e6a)' }}><CheckCircle2 size={20} /></div>
          <p className="k-stat-value">{stats.todayTasks}</p>
          <p className="k-stat-label">Today's Tasks</p>
        </div>
        <div className="k-stat-card">
          <div className="k-stat-icon" style={{ color: 'var(--gold-500,#c8a03c)' }}><CheckCircle2 size={20} /></div>
          <p className="k-stat-value">{stats.completedTasks}</p>
          <p className="k-stat-label">Completed</p>
        </div>
      </div>

      {/* Search */}
      <div ref={filterSentinelRef} aria-hidden="true" style={{ height: 1, marginBottom: -1 }} />
      <div className={`k-card k-filter-sticky ${isFilterStuck ? 'is-stuck' : ''}`}>
        <div className="k-filter-bar">
          <div className="k-search-wrap" style={{ flex: 1 }}>
            <Search size={14} className="k-search-icon" />
            <input type="text" placeholder="Search tasks or descriptions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="k-search" />
          </div>
        </div>
      </div>

      {/* Task sections */}
      {groupedTasks.today.length === 0 && groupedTasks.overdue.length === 0 ? (
        <div className="k-card">
          <div className="k-empty">
            <CheckSquare size={36} className="k-empty-icon" />
            <p className="k-empty-title">No Checklist Tasks Found</p>
            <p className="k-empty-msg">{fmsData && fmsData.length === 0 ? `No checklist tasks assigned to ${currentUser.name} as Doer` : 'No tasks match your current search'}</p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Today's tasks */}
          {groupedTasks.today.length > 0 && (
            <div className="k-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(var(--forest-400-rgb,74,158,106),0.06)' }}>
                <div className="k-stat-icon" style={{ width: '32px', height: '32px', borderRadius: '8px', color: 'var(--forest-400,#4a9e6a)' }}><CheckSquare size={15} /></div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Today's Tasks</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{groupedTasks.today.length} task{groupedTasks.today.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="k-task-list">
                {groupedTasks.today.map((task, i) => renderChecklistRow(task, i, false))}
              </div>
            </div>
          )}

          {/* Overdue tasks */}
          {groupedTasks.overdue.length > 0 && (
            <div className="k-card" style={{ overflow: 'hidden', border: '1px solid var(--status-error-border,rgba(239,68,68,0.3))' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(239,68,68,0.05)' }}>
                <div className="k-stat-icon" style={{ width: '32px', height: '32px', borderRadius: '8px', color: 'var(--status-error)' }}><AlertTriangle size={15} /></div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Overdue Tasks</p>
                  <p style={{ fontSize: '12px', color: 'var(--status-error)', margin: 0 }}>{groupedTasks.overdue.length} task{groupedTasks.overdue.length !== 1 ? 's' : ''} overdue</p>
                </div>
              </div>
              <div className="k-task-list">
                {groupedTasks.overdue.map((task, i) => renderChecklistRow(task, i, true))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChecklistTasks;
